import dayjs from "../../../util/dayjs";

import { networkName, locality } from "../../../util/formatter";

/* Network name → a stable hue, so the identity tile is the same colour every time a point of that
   network is opened (the mockup's `--h` from `netHue`). A plain char-sum hash is enough — it only
   needs to be deterministic and spread, not cryptographic. */
const hueOf = name => {
  const s = String(name || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
};

const networkLabel = item => (item.network && typeof item.network === "object" ? item.network.name : "");

export default {
  slug: "points",
  endpoint: "points/",
  titleKey: "points_title",
  subtitleKey: "points_sub",
  columns: [
    { label: "f_network", format: row => networkName(null, row) },
    { field: "name", label: "f_name" },
    { field: "address", label: "f_address" },
    { field: "code", label: "f_code" },
    { label: "f_locality", format: row => locality(null, row) },
  ],
  filters: [
    { name: "network", entity: "network" },
    { name: "locality__county", entity: "county", label: "f_county", defaultRegion: true },
    { name: "locality", entity: "locality", label: "f_locality", scopeBy: "locality__county" },
  ],
  fields: [
    { name: "name", type: "text", label: "f_name", required: true },
    { name: "code", type: "text", label: "f_code" },
    { name: "address", type: "text", label: "f_address", wide: true },
    { name: "network", type: "entity", entity: "network", label: "f_network", required: true },
    { name: "category", type: "entity", entity: "categories_point", label: "f_category" },
    { name: "locality", type: "entity", entity: "locality", label: "f_locality" },
    // Required: a point with no coordinates can't be geofenced, so a visit to it can never be
    // verified on site (the same gap the list flags as "No coordinates"). `isEmpty(null)` catches
    // an unset location; a set one is `{latitude, longitude}`, which passes.
    { name: "location", type: "location", label: "f_location", wide: true, required: true },
    { name: "minutes_recommended", type: "number", label: "f_minutes" },
    { name: "responsible_merchandiser", type: "entity", entity: "merchandiser", label: "f_merchandiser" },
    { name: "responsible_supervisors", type: "entity", entity: "supervisor", isMulti: true, label: "f_supervisors" },
    { name: "is_active", type: "checkbox", label: "f_is_active" },
    { name: "is_available_for_task_creation", type: "checkbox", label: "f_available_tasks" },
  ],
  sections: [
    { title: "sec_basic", desc: "sec_basic_desc", fields: ["name", "code", "network", "category"] },
    { title: "sec_location", desc: "sec_location_desc", fields: ["address", "locality", "location"] },
    { title: "sec_assignment", desc: "sec_assignment_desc", fields: ["responsible_merchandiser", "responsible_supervisors"] },
    { title: "sec_options", desc: "sec_options_desc", fields: ["minutes_recommended", "is_active", "is_available_for_task_creation"] },
  ],
  softDelete: true,

  /* ---- points-only detail-page hooks (docs/design/point-detail.html) ----
     These are optional on a descriptor; only points sets them, so the other nine reference
     entities render the plain identity strip + rail unchanged. */

  /* The identity tile: a 2-letter chip tinted by the network, not the shared initials avatar. */
  identityLogo: item => {
    const net = networkLabel(item);
    return { text: (net || item.name || "—").slice(0, 2).toUpperCase(), hue: hueOf(net) };
  },

  /* Visits / Last visit — the one real activity aggregate, shown in the identity strip and the
     rail Summary. `visits_count` / `last_visit` come from the detail serializer (annotated on the
     queryset); absent on an unsaved point, hence the fallbacks. */
  metrics: [
    { key: "visits", labelKey: "visits_col", value: item => item.visits_count ?? 0 },
    {
      key: "last_visit",
      labelKey: "last_visit",
      value: item => (item.last_visit ? dayjs(item.last_visit).format("DD MMM YYYY") : "—"),
    },
  ],

  /* Soft warnings for the rail's Data quality — real gaps that do not block a save the way a
     required field does. "Unassigned" is the same signal the list flags. */
  warnings: [{ key: "unassigned", field: "responsible_merchandiser", labelKey: "w_no_merch", when: item => !item.responsible_merchandiser }],

  /* The coordinate editor is hoisted out of the Location section into a hero card at the top of
     the form (the mockup's `pdMap`), so the map leads the page. It is the only map on this page —
     the old read-only `detailPanel` was unreachable (no descriptor ever set `readOnly`) and went
     with `ResourceView`. */
  heroField: "location",
};
